/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import { styled, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import Avatar, { AVATAR_SIZES } from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, { DropdownItemCheckbox, type OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { VARIATION } from '@atlassian/jira-common-util-get-experiment-group/src/index.tsx';
import { useRealtimePresenceExperiment } from '@atlassian/jira-real-time-presence/src/controllers/use-realtime-presence-experiment/index.tsx';
import { type AccountId, toAccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type { Person } from '../../../../../common/types.tsx';
import { Z_INDEX } from '../../../../../common/ui/assignee/constants.tsx';
import PresenceTooltip from '../../../../../common/ui/presence-tooltip/index.tsx';

import {
	AvatarWrapper,
	ActionAvatar,
	ActionAvatarSpacer,
	AvatarBorder,
} from '../../../../../common/ui/assignee/index.tsx';

export type Props = {
	users: Person[];
	selectedUsers: AccountId[];
	onItemActivated: (userId: AccountId) => void;
	onOpen: () => void;
	onClose?: () => void;
	isDisabled?: boolean;
};

const ShowMoreButton = (props: Props) => {
	const {
		users,
		selectedUsers,
		onOpen,
		onItemActivated: onItemActivatedProps,
		onClose,
		isDisabled = false,
	} = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onOpenChange = useCallback(
		({ isOpen: isOpenDropDown }: OnOpenChangeArgs) => {
			if (isOpenDropDown) {
				onOpen();
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'showMoreAssignee');
			} else {
				onClose && onClose();
			}
		},
		[onOpen, createAnalyticsEvent, onClose],
	);

	const isSelected = useMemo(
		() => users.some((user) => selectedUsers.includes(user.id)),
		[users, selectedUsers],
	);

	const { experimentCohort } = useRealtimePresenceExperiment();

	const isRealtimePresenceEnabled = experimentCohort === VARIATION;

	const dropdownItems = useMemo(
		() =>
			users.map(({ id, displayName, avatarUrl, presence }) => (
				<ItemWrapper key={id}>
					<DropdownItemCheckbox
						id={id}
						isSelected={selectedUsers.includes(id)}
						onClick={() => {
							onItemActivatedProps(toAccountId(String(id)));
						}}
					>
						<ItemContent>
							{isRealtimePresenceEnabled ? (
								<PresenceTooltip content={displayName} presence={presence}>
									<DropdownAvatarWrapper>
										<Avatar size="small" src={avatarUrl} name={displayName} presence={presence} />
									</DropdownAvatarWrapper>
								</PresenceTooltip>
							) : (
								<Tooltip content={displayName}>
									<DropdownAvatarWrapper>
										<Avatar size="small" src={avatarUrl} name={displayName} presence={presence} />
									</DropdownAvatarWrapper>
								</Tooltip>
							)}
							<TextContentWrapper>
								<TextContent>{displayName}</TextContent>
							</TextContentWrapper>
						</ItemContent>
					</DropdownItemCheckbox>
				</ItemWrapper>
			)),
		[users, selectedUsers, onItemActivatedProps, isRealtimePresenceEnabled],
	);

	const showMoreButtonAvatarSpacer = (
		<ShowMoreButtonAvatarSpacer>{`+${users.length}`}</ShowMoreButtonAvatarSpacer>
	);

	return (
		<DropdownMenuWrapper>
			<DropdownMenu<HTMLDivElement>
				onOpenChange={onOpenChange}
				trigger={({ triggerRef, 'aria-expanded': ariaExpanded, testId: _, ...triggerProps }) => (
					<AvatarWrapper
						{...triggerProps}
						as="button"
						aria-expanded={ariaExpanded}
						ref={triggerRef}
						zIndex={Z_INDEX.MENU}
						data-testid="filters.ui.filters.assignee.stateless.show-more-button.assignee-filter-show-more"
						{...(fg('list_lovability_improving_filters') && {
							isDisabled,
							disabled: isDisabled,
							'aria-disabled': isDisabled,
						})}
					>
						<AvatarBorder selected={isSelected}>
							<ActionAvatar
								as="div"
								tabIndex={-1}
								{...(fg('list_lovability_improving_filters') && {
									isDisabled,
									'aria-disabled': isDisabled,
									disabled: isDisabled,
								})}
							>
								{isVisualRefreshEnabled() ? (
									showMoreButtonAvatarSpacer
								) : (
									<ActionAvatarSpacer>{`+${users.length}`}</ActionAvatarSpacer>
								)}
							</ActionAvatar>
						</AvatarBorder>
					</AvatarWrapper>
				)}
			>
				<DropdownItemsWrapper>{dropdownItems}</DropdownItemsWrapper>
			</DropdownMenu>
		</DropdownMenuWrapper>
	);
};

ShowMoreButton.defaultProps = {
	onItemActivated: noop,
	onOpen: noop,
};

export default ShowMoreButton;

const dropdownMenuWrapperStyle = cssMap({
	root: {
		display: 'inline-flex',
		alignItems: 'center',
		marginLeft: token('space.negative.100'),
		zIndex: 1,
	},
});

const DropdownMenuWrapper = ({ children }: { children: ReactNode }) => {
	return <div css={[dropdownMenuWrapperStyle.root]}>{children}</div>;
};

// The old menu dropdown has a max-width of 300px but the new one has 800px.
// We have to set the max-width in the wrapper to keep the same style.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemsWrapper = styled.div({
	maxWidth: '300px',
	paddingTop: token('space.050'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.0'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button': {
		paddingTop: token('space.0'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.150'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& span': {
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemContent = styled.div({
	display: 'flex',
	alignItems: 'center',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownAvatarWrapper = styled.div({
	display: 'flex',
	marginLeft: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContentWrapper = styled.div({
	display: 'flex',
	marginTop: token('space.0'),
	marginRight: token('space.100'),
	marginBottom: token('space.0'),
	marginLeft: token('space.100'),
	paddingTop: token('space.100'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.0'),
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContent = styled.div({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ShowMoreButtonAvatarSpacer = styled(ActionAvatarSpacer)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${AVATAR_SIZES.small}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${AVATAR_SIZES.small}px`,
	font: token('font.body.small'),
});
